import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE, Language, languages } from "../../../core/config";
import { highchartsOptionsConfigurationBase, seriesOptionsConfigurationBase } from "../config/TimeSeriesStatistics.config";
import { TimeSeriesMeasurementPoint } from "../model/TimeSeriesMeasurement.model";
import { ChartSettings, Resolution, Timespan } from "../../chart-settings/model";
import { formatNumberWithTwoFractionDigits } from "../NumberFormatter";
import { useTheme } from "@mui/material";
import { YAxisOptions } from "highcharts";

export type SeriesConfiguration = {
  seriesData: TimeSeriesMeasurementPoint[],
  seriesName: string
}

type TooltipContextPoint = {
  name: string;
  x: number,
  y?: number|undefined,
};

const yAxisLimitedTo100Percent: YAxisOptions = {
  ...highchartsOptionsConfigurationBase.yAxis,
  max: 100
};

export const useTimeSeriesStatisticsHighchartsOptions = (
  chartSettings: ChartSettings,
  unit: string,
  seriesConfigurations: SeriesConfiguration[],
  tooltipPointValueFormatter?: (value?: number) => string
): Highcharts.Options => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const colors = [theme.palette.primary.dark, theme.palette.secondary.dark];
  const minumumTimeFromSeries = getMinumumTimeFromSeriesConfigurations(seriesConfigurations);
  const maximumTimeFromSeries = getMaximumTimeFromSeriesConfigurations(seriesConfigurations);
  const locale = getDefaultLocaleForLanguage(i18n.language) || getLocale(DEFAULT_LANGUAGE);
  const yAxis = unit === '%' ? yAxisLimitedTo100Percent : highchartsOptionsConfigurationBase.yAxis;
  const showSeriesInLegend = seriesConfigurations.length > 1;
  return {
    ...highchartsOptionsConfigurationBase,
    lang: {
      locale
    },
    legend: {
      enabled: showSeriesInLegend
    },
    navigator: {
      enabled: true,
      xAxis: {
        min: minumumTimeFromSeries,
        max: maximumTimeFromSeries
      }
    },
    series: seriesConfigurations.map((seriesConfiguration, seriesIndex) => buildSeriesOptions(
      seriesConfiguration.seriesName,
      seriesConfiguration.seriesData,
      chartSettings.timespan,
      colors[seriesIndex])),
    tooltip: {
      formatter: function() {
        return formatTooltip(this, chartSettings, unit, locale, tooltipPointValueFormatter);
      }
    },
    xAxis: {
      type: 'datetime',
      min: minumumTimeFromSeries,
      max: maximumTimeFromSeries
    },
    yAxis
  }
};

const getMinumumTimeFromSeriesConfigurations = (seriesConfigurations: SeriesConfiguration[]): number|undefined => {
  const seriesMinimums = seriesConfigurations
    .map(seriesConfiguration => getMinumumDateFromSeries(seriesConfiguration.seriesData))
    .filter(isNumber);
  return Math.min(...seriesMinimums);
};

const getMaximumTimeFromSeriesConfigurations = (seriesConfigurations: SeriesConfiguration[]): number|undefined => {
  const seriesMaximums = seriesConfigurations
    .map(seriesConfiguration => getMaximumDateFromSeries(seriesConfiguration.seriesData))
    .filter(isNumber);
  return Math.min(...seriesMaximums);
};

const isNumber = (value: number | undefined): value is number => typeof value === 'number';

const getMinumumDateFromSeries = (seriesData: TimeSeriesMeasurementPoint[]): number|undefined => getDateFromMeasurement(seriesData[0]);

const getMaximumDateFromSeries = (seriesData: TimeSeriesMeasurementPoint[]): number|undefined => {
  const lastMeasurement = seriesData[seriesData.length - 1];
  return getDateFromMeasurement(lastMeasurement);
};

const getDateFromMeasurement = (measurement: TimeSeriesMeasurementPoint): number | undefined => {
  return measurement === undefined ? undefined : measurement.x;
};

const getDefaultLocaleForLanguage = (languageCode: string): string|undefined => {
  const language = languages.find(language => language.code === languageCode);
  return language === undefined ? undefined : getLocale(language);
};

const getLocale = (language: Language): string => `${language.code}-${language.countryCode}`;

const buildSeriesOptions = (
  seriesName: string,
  seriesData: TimeSeriesMeasurementPoint[],
  timespan: Timespan,
  color: string
): Highcharts.SeriesAreaOptions => ({
  ...seriesOptionsConfigurationBase,
  name: seriesName,
  data: seriesData,
  color,
  marker: {
    enabled: timespan === Timespan.FiveMinutes,
    states: {
      hover: {
        fillColor: color,
        lineColor: color,
      }
    }
  }
});

const formatTooltip = (
  point: TooltipContextPoint,
  chartSettings: ChartSettings,
  unit: string,
  locale: string,
  pointValueFormatter?: (value?: number) => string
): string|undefined => {
  const dateTime = new Date(point.x);
  const dateTimeFormatted = formatTimeLabel(dateTime, locale, chartSettings);
  const pointValue = point.y;
  const pointValueFormatted
     = pointValueFormatter === undefined
     ? `${formatNumberWithTwoFractionDigits(pointValue, locale, '?')} ${unit}`
     : pointValueFormatter(pointValue);
  return `
      <span style="font-size: 0.8em">${dateTimeFormatted}</span><br/>
      <span style="font-weight: bold">${displayPointNameWithColonIfProvided(point)}${pointValueFormatted}</span>`;
};

const formatTimeLabel = (time: Date, locale: string, chartSettings: ChartSettings): string => {
  const formatter = Intl.DateTimeFormat(locale, getTimeLabelConfiguration(chartSettings));
  return formatter.format(time);
};

const displayPointNameWithColonIfProvided = (point: TooltipContextPoint): string => point.name === undefined ? '' : `${point.name}: `;

const getTimeLabelConfiguration = (chartSettings: ChartSettings): Intl.DateTimeFormatOptions => {
  const timeWithSecondsFormat: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
  const timeWithMinutesFormat: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const dateFormat: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  const timeWithSecondsAndDateFormat: Intl.DateTimeFormatOptions = { ...timeWithSecondsFormat, ...dateFormat };
  const timeWithMinutesAndDateFormat: Intl.DateTimeFormatOptions = { ...timeWithMinutesFormat, ...dateFormat };
  const timespanAndResolutionConfigMap: { [P in Timespan]: Partial<{ [K in Resolution]: Intl.DateTimeFormatOptions }> } = {
    [Timespan.FiveMinutes]: {
      [Resolution.FiveMinutes]: timeWithSecondsFormat
    },
    [Timespan.Hour]: {
      [Resolution.FiveMinutes]: timeWithSecondsAndDateFormat
    },
    [Timespan.Day]: {
      [Resolution.FiveMinutes]: timeWithSecondsAndDateFormat,
      [Resolution.Hour]: timeWithMinutesAndDateFormat,
      [Resolution.SixHours]: timeWithMinutesAndDateFormat
    },
    [Timespan.Week]: {
      [Resolution.FiveMinutes]: timeWithSecondsAndDateFormat,
      [Resolution.Hour]: timeWithMinutesAndDateFormat,
      [Resolution.SixHours]: timeWithMinutesAndDateFormat,
      [Resolution.Day]: dateFormat
    },
    [Timespan.Month]: {
      [Resolution.FiveMinutes]: timeWithSecondsAndDateFormat,
      [Resolution.Hour]: timeWithMinutesAndDateFormat,
      [Resolution.SixHours]: timeWithMinutesAndDateFormat,
      [Resolution.Day]: dateFormat,
      [Resolution.Week]: dateFormat
    },
    [Timespan.Year]: {
      [Resolution.Hour]: timeWithMinutesAndDateFormat,
      [Resolution.SixHours]: timeWithMinutesAndDateFormat,
      [Resolution.Day]: dateFormat,
      [Resolution.Week]: dateFormat
    }
  };
  const timespanConfig = timespanAndResolutionConfigMap[chartSettings.timespan];
  const resolutionConfig = timespanConfig[chartSettings.resolution];
  return resolutionConfig || timeWithSecondsAndDateFormat
};